import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import type { Referral } from '@/hooks/useReferrals';
import { useClinic } from '@/hooks/useClinicSettings';
import { useClinicCommunicateConnection } from '@/hooks/useCareCommunications';
import { useSorBySorId } from '@/hooks/useSorLookup';
import { SorPicker, type PickedReceiver } from '@/components/patient/ReferralSorPicker';
import { ReferralReceiptBadge } from '@/components/patient/ReferralReceiptBadge';
import { Button } from '@/components/ui/Button';
import { ApiCallError, callFunction } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { readClinicMedicalSpecialityCode } from '@/lib/referral/clinicMedicalSpecialityCode';
import { downloadLatin1Xml } from '@/lib/referral/latin1Xml';
import { formatReferralDateTime } from '@/lib/referral/referralDateTime';
import {
  ALL_VISITATION_CODES,
  REVISIT_CODES,
  REVISIT_MAX_ELEMENTS,
  REVISIT_TOTAL_MAX,
  isRevisitCode,
  receivedVisitationLines,
  revisitBudget,
  revisitTextShape,
  type RevisitCode,
  type RevisitTextRow,
} from '@/lib/referral/revisitText';
import {
  readPostalAddress,
  receiverIdentifierPair,
  type ReceiverIdentifierParty,
} from '@/lib/referral/xref01Validation';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

// C6 (REF01-M 3.18–3.30): viderevisitering of a received XREF01. Communicate
// builds the new XREF01 from the received XML: the referral text, diagnoses,
// same/new illness, hospital choice and LPR3 UUID are copied unchanged, the
// old sender moves to Referrer, and HospitalVisitation gets the texts below
// after the henvisning_modtaget element.

const SECTION =
  'overflow-hidden rounded-lg border border-ds-line bg-ds-card shadow-card  ';
const HEADING = 'text-xs font-medium uppercase tracking-wide text-ds-muted ';
const FIELD =
  'mt-1 block w-full rounded-md border border-ds-field px-3 py-2 shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal   ';

type RevisitRow = {
  id: string;
  exchange_id: string;
  envelope_identifier: string;
  receiver_organisation_name: string | null;
  receiver_ean: string;
  status: string;
  created_at: string;
};

type Check = { id: string; pass: boolean; detail: string };
type RevisitResult = { id: string; envelopeIdentifier: string; ok: boolean; xml: string; checks: Check[] };

type RevisitRequest = {
  action: 'revisit';
  clinicId: string;
  referralId: string;
  sender: Record<string, string>;
  receiver: Record<string, string>;
  texts: { informationCode: RevisitCode; text: string }[];
};

function codeLabelKey(code: string): string {
  return `referrals.revisit_code_${code}`;
}

const CODE_FALLBACK: Record<string, string> = {
  henvisning_modtaget: 'Henvisning modtaget (HV)',
  visitering: 'Visitering (VI)',
  indkaldt: 'Indkaldt (IK)',
  indlagt_paa: 'Indlagt på (AF)',
  udenamts_kaution: 'Udenamts/Kaution (UA)',
  egen_laege: 'Egen læge (EL)',
  ustruktureret: 'Ustruktureret tekst (US)',
};

function identifierParty(picked: PickedReceiver): ReceiverIdentifierParty {
  return {
    registerCode: picked.organisation.identifierCode,
    chosenCode: picked.chosenIdentifierCode,
    identifier: picked.identifier,
  };
}

function filled(entries: Record<string, string | null | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(entries)) {
    if (typeof value === 'string' && value.trim() !== '') out[key] = value.trim();
  }
  return out;
}

function useReferralRevisits(referralId: string) {
  return useQuery({
    queryKey: ['referral-revisits', referralId],
    queryFn: async (): Promise<RevisitRow[]> => {
      const { data, error } = await supabase
        .from('clinic_communicate_outbound_referrals')
        .select('id, exchange_id, envelope_identifier, receiver_organisation_name, receiver_ean, status, created_at')
        .eq('source_referral_id', referralId)
        // XDIS13/XDIS91 answers share source_referral_id; they are listed in ReferralReplyPanel.
        .eq('kind', 'XREF01')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as RevisitRow[];
    },
  });
}

export function ReferralRevisitPanel({ referral }: { referral: Referral }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const clinicQuery = useClinic(referral.clinic_id);
  const connectionQuery = useClinicCommunicateConnection(referral.clinic_id);
  const senderSor = useSorBySorId(connectionQuery.data?.sor_id ?? null);
  const revisits = useReferralRevisits(referral.id);
  const [open, setOpen] = useState(false);
  const [receiver, setReceiver] = useState<PickedReceiver | null>(null);
  const [rows, setRows] = useState<RevisitTextRow[]>([{ code: 'visitering', text: '' }]);
  const [attempted, setAttempted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RevisitResult | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const details = referral.medcom_details;
  const received = receivedVisitationLines(details.hospitalVisitations, referral.received_at ?? referral.created_at);
  const budget = revisitBudget(rows, received.lines);
  const medicalSpecialityCode = readClinicMedicalSpecialityCode(connectionQuery.data?.medical_speciality_code);
  const receiverPair = receiver ? receiverIdentifierPair(identifierParty(receiver)) : null;

  const senderGap = !connectionQuery.data?.ean
    ? t('referrals.revisit_sender_missing_ean', 'Klinikken har ingen MedCom-forbindelse med lokationsnummer.')
    : !senderSor.data?.identifier || !senderSor.data.identifierCode
      ? t('referrals.revisit_sender_missing_sor', 'Klinikkens SOR-enhed mangler identifikator.')
      : !medicalSpecialityCode
        ? t('referrals.revisit_sender_missing_speciality', 'Klinikkens speciale (MedicalSpecialityCode) er ikke konfigureret.')
        : null;

  function updateRow(index: number, next: Partial<RevisitTextRow>) {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...next } : row)));
  }

  async function onDownload(exchangeId: string, envelope: string, rowId: string) {
    setBusyId(rowId);
    try {
      const payload = await callFunction<
        { action: 'payload'; clinicId: string; exchangeId: string },
        { xml?: string }
      >('communicate-ref', { action: 'payload', clinicId: referral.clinic_id, exchangeId });
      if (payload.xml === undefined) throw new Error('Missing XML payload');
      downloadLatin1Xml(payload.xml, `XREF01_viderevisiteret_${envelope}.xml`);
    } catch (err) {
      toast.error(
        err instanceof ApiCallError
          ? err.message
          : t('patients.hospital_referral.outbox_download_failed', 'XML-filen kunne ikke hentes.'),
      );
    } finally {
      setBusyId(null);
    }
  }

  async function onSubmit() {
    setAttempted(true);
    setError(null);
    if (!receiver || !receiverPair?.ok || !budget.ok || senderGap) return;
    const clinic = clinicQuery.data;
    const sor = senderSor.data;
    const connection = connectionQuery.data;
    if (!clinic || !sor?.identifier || !sor.identifierCode || !connection?.ean || !medicalSpecialityCode) return;
    const clinicAddress = readPostalAddress(clinic.address);
    const body: RevisitRequest = {
      action: 'revisit',
      clinicId: referral.clinic_id,
      referralId: referral.id,
      sender: filled({
        ean: connection.ean,
        identifier: sor.identifier,
        identifierCode: sor.identifierCode,
        organisationName: clinic.name,
        streetName: clinicAddress.street,
        postCodeIdentifier: clinicAddress.postCode,
        districtName: clinicAddress.district,
        telephoneSubscriberIdentifier: clinic.phone,
        medicalSpecialityCode,
      }),
      receiver: filled({
        ean: receiver.organisation.ean,
        identifier: receiver.identifier,
        identifierCode: receiverPair.identifierCode,
        organisationName: receiver.organisation.name,
        streetName: receiver.organisation.address.street,
        postCodeIdentifier: receiver.organisation.address.postCode,
        districtName: receiver.organisation.address.district,
      }),
      texts: rows.map((row) => ({ informationCode: row.code, text: row.text })),
    };
    setPending(true);
    try {
      const built = await callFunction<RevisitRequest, RevisitResult>('communicate-ref', body);
      setResult(built);
      await queryClient.invalidateQueries({ queryKey: ['referral-revisits', referral.id] });
      if (referral.patient_id) {
        await queryClient.invalidateQueries({
          queryKey: ['hospital-referral-outbox', referral.clinic_id, referral.patient_id],
        });
      }
      toast.success(t('referrals.revisit_built', 'Den viderevisiterede henvisning er dannet og ligger i udbakken.'));
      setOpen(false);
      setRows([{ code: 'visitering', text: '' }]);
      setReceiver(null);
      setAttempted(false);
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : t('referrals.revisit_failed', 'Henvisningen kunne ikke viderevisiteres.'));
    } finally {
      setPending(false);
    }
  }

  const lpr = Array.isArray(details.episodesOfCare)
    ? (details.episodesOfCare as Record<string, unknown>[]).find((e) =>
        String(e?.type ?? '').toUpperCase().startsWith('LPR'),
      )
    : undefined;
  const carried = [
    [t('referrals.xref01_diagnosis_kind'), typeof details.referDescriptionCode === 'string' ? details.referDescriptionCode : null],
    [t('referrals.xref01_hospital_choice'), typeof details.hospitalChoice === 'string' ? details.hospitalChoice : null],
    [t('referrals.xref01_lpr3_uuid'), typeof lpr?.identifier === 'string' ? lpr.identifier : null],
  ] as const;

  return (
    <section className={SECTION} aria-labelledby="referral-revisit-heading">
      <div className="flex items-center justify-between gap-3 border-b border-ds-line px-4 py-2.5">
        <span id="referral-revisit-heading" className={HEADING}>
          {t('referrals.revisit_title', 'Viderevisitér henvisningen')}
        </span>
        {!open && (
          <Button type="button" size="sm" onClick={() => setOpen(true)} disabled={!referral.patient_id}>
            {t('referrals.revisit_open', 'Viderevisitér')}
          </Button>
        )}
      </div>

      {!referral.patient_id && (
        <p className={cn('px-4 py-3', FORM_HINT_TYPE)}>
          {t('referrals.revisit_needs_patient', 'Knyt henvisningen til en patient, før den viderevisiteres.')}
        </p>
      )}

      {open && (
        <div className="space-y-5 px-4 py-4">
          <p className={FORM_HINT_TYPE}>
            {t(
              'referrals.revisit_locked_text',
              'Den fremsendte henvisningstekst, diagnoser og anamnese sendes uændret med og kan ikke rettes. Den nuværende afsender flyttes til henviser, og klinikken bliver ny afsender.',
            )}
          </p>
          <dl className="grid gap-2 text-sm sm:grid-cols-3">
            {carried.map(([label, value]) => (
              <div key={label}>
                <dt className={HEADING}>{label}</dt>
                <dd className="mt-1 break-words text-ds-text">{value ?? '—'}</dd>
              </div>
            ))}
          </dl>
          {senderGap && (
            <p role="alert" className={FORM_ERROR_TYPE}>
              {senderGap}
            </p>
          )}

          <div>
            <SorPicker
              label={t('referrals.revisit_receiver', 'Ny modtager (anden afdeling)')}
              fieldId="revisit-receiver"
              selected={receiver}
              partyLabel={t('patients.hospital_referral.receiver_section', 'Modtager')}
              receives="XREF01"
              onSelect={(organisation) =>
                setReceiver({ organisation, identifier: organisation.identifier ?? '', chosenIdentifierCode: null })
              }
              onIdentifier={(identifier) => setReceiver((current) => (current ? { ...current, identifier } : current))}
              onIdentifierCode={(chosenIdentifierCode) =>
                setReceiver((current) => (current ? { ...current, chosenIdentifierCode } : current))
              }
              onClear={() => setReceiver(null)}
            />
            {attempted && (!receiver || !receiverPair?.ok) && (
              <p role="alert" className={cn('mt-2', FORM_ERROR_TYPE)}>
                {t('referrals.revisit_receiver_missing', 'Vælg en modtager med identifikator og identifikatorkode.')}
              </p>
            )}
          </div>

          <div>
            <span className={cn('block', FORM_LABEL_TYPE)}>
              {t('referrals.revisit_codes_title', 'Forbeholdt sygehuset (HospitalVisitation)')}
            </span>
            <div className="mt-2 rounded-md border border-ds-line bg-ds-sunk px-3 py-2">
              <div className={HEADING}>
                {t(codeLabelKey('henvisning_modtaget'), CODE_FALLBACK.henvisning_modtaget)}
              </div>
              <pre className="mt-1 whitespace-pre-wrap font-mono text-sm text-ds-text">
                {received.lines.join('\n')}
              </pre>
              <p className={cn('mt-1', FORM_HINT_TYPE)}>
                {received.kept
                  ? t('referrals.revisit_received_kept', 'Den primære henvisningsdato bevares først og kan ikke slettes. Tidligere visiteringstekst fjernes.')
                  : t('referrals.revisit_received_created', 'Henvisningsdatoen indsættes først med koden henvisning_modtaget og kan ikke slettes.')}
              </p>
            </div>

            {rows.map((row, index) => {
              const shape = revisitTextShape(row);
              const empty = attempted && shape.elements === 0;
              return (
                <div key={index} className="mt-3 rounded-md border border-ds-line p-3">
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label htmlFor={`revisit-code-${index}`} className={cn('block', FORM_LABEL_TYPE)}>
                        {t('referrals.revisit_code', 'Visitationskode (TEXTNR)')}
                      </label>
                      <select
                        id={`revisit-code-${index}`}
                        value={row.code}
                        onChange={(event) => {
                          if (isRevisitCode(event.target.value)) updateRow(index, { code: event.target.value });
                        }}
                        className={cn(TYPE.inputValue, FIELD)}
                      >
                        {ALL_VISITATION_CODES.map((code) => (
                          <option key={code} value={code} disabled={code === 'henvisning_modtaget'}>
                            {t(codeLabelKey(code), CODE_FALLBACK[code])}
                            {code === 'henvisning_modtaget'
                              ? ` — ${t('referrals.revisit_code_automatic', 'sættes automatisk')}`
                              : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {rows.length > 1 && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        aria-label={t('referrals.revisit_remove_text', 'Fjern tekst')}
                        onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <label htmlFor={`revisit-text-${index}`} className={cn('mt-2 block', FORM_LABEL_TYPE)}>
                    {t('referrals.revisit_text', 'Tekst')}
                  </label>
                  <textarea
                    id={`revisit-text-${index}`}
                    rows={5}
                    value={row.text}
                    onChange={(event) => updateRow(index, { text: event.target.value })}
                    aria-invalid={empty}
                    className={cn(TYPE.inputValue, FIELD, 'font-mono', empty && 'border-ds-red')}
                  />
                  <p className={cn('mt-1', FORM_HINT_TYPE)}>
                    {t('referrals.revisit_text_shape', '{{characters}} tegn, {{lines}} linjer à 70, {{elements}} felt(er)', {
                      characters: shape.characters,
                      lines: shape.lines.length,
                      elements: shape.elements,
                    })}
                  </p>
                </div>
              );
            })}
            {rows.length < REVISIT_CODES.length + 2 && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-3"
                onClick={() => setRows((current) => [...current, { code: 'visitering', text: '' }])}
              >
                <Plus className="mr-1 h-4 w-4" />
                {t('referrals.revisit_add_text', 'Tilføj tekst med anden kode')}
              </Button>
            )}
            <p
              className={cn('mt-3 text-sm', budget.overCharacters || budget.overElements ? 'text-ds-red-ink' : 'text-ds-muted ')}
              aria-live="polite"
            >
              {t('referrals.revisit_budget', '{{characters}} / {{max}} tegn · {{elements}} / {{maxElements}} felter (9 × 5 linjer à 70 tegn)', {
                characters: budget.characters,
                max: REVISIT_TOTAL_MAX,
                elements: budget.elements,
                maxElements: REVISIT_MAX_ELEMENTS,
              })}
            </p>
            {(budget.overCharacters || budget.overElements) && (
              <p role="alert" className={FORM_ERROR_TYPE}>
                {t('referrals.revisit_over_budget', 'Teksten er for lang. Feltet Forbeholdt sygehuset må højst fylde 3150 tegn eller 9 felter.')}
              </p>
            )}
            {attempted && budget.emptyRows > 0 && (
              <p role="alert" className={FORM_ERROR_TYPE}>
                {t('referrals.revisit_empty_text', 'Skriv en tekst for hver valgt kode, eller fjern rækken.')}
              </p>
            )}
          </div>

          {error && (
            <p role="alert" className={FORM_ERROR_TYPE}>
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="button" onClick={() => void onSubmit()} disabled={pending || Boolean(senderGap)}>
              {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('referrals.revisit_submit', 'Dan viderevisiteret henvisning')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setOpen(false)} disabled={pending}>
              {t('common.cancel', 'Annuller')}
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="border-t border-ds-line px-4 py-3 text-sm">
          <ul className="space-y-1">
            {result.checks.map((check) => (
              <li key={check.id} className={check.pass ? 'text-ds-green-ink ' : 'text-ds-red-ink'}>
                {check.pass ? '✓' : '✗'} {check.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(revisits.data?.length ?? 0) > 0 && (
        <div className="border-t border-ds-line px-4 py-3">
          <div className={HEADING}>{t('referrals.revisit_sent', 'Viderevisiteringer')}</div>
          <ul className="mt-2 divide-y divide-ds-line">
            {revisits.data?.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <span className="text-ds-text">
                  {row.receiver_organisation_name ?? row.receiver_ean} · {row.envelope_identifier} ·{' '}
                  {formatReferralDateTime(row.created_at)}
                </span>
                <span className="flex items-center gap-2">
                  <ReferralReceiptBadge status={row.status} />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={busyId === row.id}
                    onClick={() => void onDownload(row.exchange_id, row.envelope_identifier, row.id)}
                  >
                    {t('referrals.revisit_download', 'Hent XML')}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
          <p className={cn('mt-1', FORM_HINT_TYPE)}>
            {t('referrals.revisit_receipts_hint', 'Kvitteringer og VANS håndteres i patientens udbakke.')}
          </p>
        </div>
      )}
    </section>
  );
}
